/**
 * Automated quality control (1.0.0-rc.8): operational QC with FFmpeg and PRISM's own data. Is the file what the spec
 * says, and where are the picture, sound, subtitle or metadata problems? It is not decode-level compliance, AS-11/DPP
 * or IMF certification, a certified photosensitivity test, auto-correction or a certified delivery report: for those,
 * a Baton or Vidchecker adapter can fill the same findings later (the QcEngine interface in packages/media).
 *
 * A profile is a set of checks, each `{enabled, severity, thresholds}`. A run checks one asset version against a
 * snapshot of a profile and records findings with in and out points (ticks), which also become segments of the
 * read-only system timecode layer `s_qc`.
 */
import { z } from 'zod';
import { TICKS_PER_SECOND } from './time.ts';
import { CueSchema, visibleText, type Cue } from './subtitles.ts';
import { ProbeResultSchema, ObjectRefSchema, LocationSpecsSchema, CONTRACT_VERSION } from './jobs.ts';

/** The read-only system timecode layer findings are written to. */
export const QC_STRATUM_ID = 's_qc';

export const QC_SEVERITIES = ['error', 'warning'] as const;
export type QcSeverity = (typeof QC_SEVERITIES)[number];

export const QC_GROUPS = ['format', 'picture', 'audio', 'structure', 'subtitles', 'metadata'] as const;
export type QcGroup = (typeof QC_GROUPS)[number];
export const QC_GROUP_LABELS: Record<QcGroup, string> = {
  format: 'Format', picture: 'Picture', audio: 'Sound', structure: 'Structure', subtitles: 'Subtitles', metadata: 'Metadata',
};

const num = (d: number, min = -1e9, max = 1e9) => z.number().min(min).max(max).default(d);
const list = <T extends z.ZodType>(item: T) => z.array(item).max(50).default([]);
const check = <T extends z.ZodRawShape>(thresholds: T, opts: { severity?: QcSeverity } = {}) => z.object({
  enabled: z.boolean().default(false),
  severity: z.enum(QC_SEVERITIES).default(opts.severity ?? 'error'),
  thresholds: z.object(thresholds).prefault({} as never),
}).prefault({});

/** Every check PRISM runs, with its thresholds. Absent values take their defaults, so a profile can name only what it changes. */
export const QcChecksSchema = z.object({
  /* Format, from the probe. */
  container: check({ allowed: list(z.string().trim().toLowerCase().min(1).max(40)) }),
  videoCodec: check({ allowed: list(z.string().trim().toLowerCase().min(1).max(40)) }),
  resolution: check({ allowed: list(z.string().trim().regex(/^\d{2,5}x\d{2,5}$/, 'Use width×height, e.g. 1920x1080')) }),
  /** Frame rates as numbers, e.g. 25, 29.97, 50. */
  frameRate: check({ allowed: list(z.number().positive().max(1000)) }),
  scan: check({ scan: z.enum(['any', 'progressive', 'interlaced']).default('any'), fieldOrder: z.enum(['any', 'tff', 'bff']).default('any') }),
  vfr: check({}),
  sampleRate: check({ allowed: list(z.number().int().positive().max(768_000)) }),
  /** Channel counts across all audio streams, e.g. EBU R123 [2, 4, 8]; a layout name (stereo, 5.1) when set. */
  channels: check({ allowed: list(z.number().int().positive().max(64)), layout: z.string().trim().max(40).default('') }),
  timecode: check({ equals: z.string().trim().max(15).default('') }, { severity: 'warning' }),
  /** A duration range, or (field) a metadata field that gives the expected duration (timecode, or a number of seconds). */
  duration: check({ minSeconds: num(0, 0), maxSeconds: num(0, 0), field: z.string().max(80).default(''), toleranceSeconds: num(1, 0, 3600) }),

  /* Picture, from one decode pass. */
  black: check({ minSeconds: num(1, 0.04, 3600), pixelThreshold: num(0.1, 0, 1), pictureThreshold: num(0.98, 0, 1), headSeconds: num(2, 0, 3600), tailSeconds: num(2, 0, 3600) }),
  freeze: check({ minSeconds: num(2, 0.04, 3600), noiseDb: num(-60, -120, 0) }),
  /** Pixels outside broadcast range (signalstats BRNG), as a percentage per frame, for at least minSeconds. */
  levels: check({ maxPercent: num(1, 0, 100), minSeconds: num(0.5, 0.04, 3600) }, { severity: 'warning' }),
  /** idet's reading of the picture compared with what the file says. */
  interlace: check({}, { severity: 'warning' }),
  /** Black bars of at least minPixels on both sides (letterbox top/bottom, pillarbox left/right). */
  letterbox: check({ minPixels: num(8, 1, 2000), minSeconds: num(2, 0.04, 3600) }, { severity: 'warning' }),

  /* Sound. */
  loudness: check({ targetLufs: num(-23, -70, 0), toleranceLu: num(1, 0, 30), maxTruePeakDbtp: num(-1, -60, 6), maxLra: num(0, 0, 60) }),
  silence: check({ thresholdDb: num(-60, -120, 0), minSeconds: num(2, 0.04, 3600), perChannel: z.boolean().default(true), headSeconds: num(0, 0, 3600), tailSeconds: num(0, 0, 3600) }),
  clipping: check({ peakDbfs: num(-0.1, -20, 0), minSeconds: num(0, 0, 3600) }),
  dcOffset: check({ maxPercent: num(1, 0, 100) }, { severity: 'warning' }),
  /** Stereo pairs: aphasemeter's phase below minPhase (−1 is opposite phase) for at least minSeconds. */
  phase: check({ minPhase: num(-0.3, -1, 1), minSeconds: num(2, 0.04, 3600) }),
  /** A channel quieter than silentDb all through (dead), or a pair (1–2, 3–4…) that is identical (duplicated). */
  deadChannels: check({ silentDb: num(-70, -150, 0), duplicates: z.boolean().default(true) }),

  /* Structure: a packet scan. */
  frames: check({ maxGapFrames: num(0, 0, 1000) }),
  timecodeTracks: check({}, { severity: 'warning' }),
  avDuration: check({ maxDifferenceSeconds: num(0.5, 0, 3600) }, { severity: 'warning' }),

  /* PRISM's own data. */
  subtitles: check({
    required: z.boolean().default(false), maxCps: num(17, 1, 100), maxLineLength: num(42, 1, 200), maxLines: num(2, 1, 10),
    minDurationSeconds: num(0.833, 0, 60), maxDurationSeconds: num(7, 0, 600), minGapFrames: num(2, 0, 100),
  }, { severity: 'warning' }),
  metadata: check({ required: z.boolean().default(true), needed: z.boolean().default(false) }),
  filename: check({ pattern: z.string().max(300).default('') }, { severity: 'warning' }),
});
export type QcChecks = z.infer<typeof QcChecksSchema>;
export type QcCheckId = keyof QcChecks;
export const QC_CHECK_IDS = Object.keys(QcChecksSchema.shape) as QcCheckId[];

/** A form field of a check's thresholds, for the profile editor. */
export interface QcThresholdField {
  key: string;
  label: string;
  type: 'number' | 'text' | 'list-text' | 'list-number' | 'boolean' | 'select';
  unit?: string;
  options?: Array<{ value: string; label: string }>;
  help?: string;
}

export interface QcCheckInfo {
  group: QcGroup;
  label: string;
  help: string;
  /** Where the work is done: the probe, the decode pass, the packet scan, or PRISM's data. */
  source: 'probe' | 'decode' | 'packets' | 'catalog';
  fields: QcThresholdField[];
}

export const QC_CHECK_INFO: Record<QcCheckId, QcCheckInfo> = {
  container: { group: 'format', label: 'Container', source: 'probe', help: 'The file format, as FFmpeg names it (mov, mxf, mp4, matroska…).', fields: [{ key: 'allowed', label: 'Allowed', type: 'list-text', help: 'Comma-separated, e.g. mov, mxf' }] },
  videoCodec: { group: 'format', label: 'Video codec', source: 'probe', help: 'The codec of the first video stream (h264, prores, dnxhd, mpeg2video…).', fields: [{ key: 'allowed', label: 'Allowed', type: 'list-text' }] },
  resolution: { group: 'format', label: 'Resolution', source: 'probe', help: 'The picture size, width×height.', fields: [{ key: 'allowed', label: 'Allowed', type: 'list-text', help: 'e.g. 1920x1080, 3840x2160' }] },
  frameRate: { group: 'format', label: 'Frame rate', source: 'probe', help: 'Frames per second.', fields: [{ key: 'allowed', label: 'Allowed', type: 'list-number', unit: 'fps', help: 'e.g. 25, 50' }] },
  scan: {
    group: 'format', label: 'Scan type and field order', source: 'probe', help: 'Progressive or interlaced, and the field order the file declares.',
    fields: [
      { key: 'scan', label: 'Scan', type: 'select', options: [{ value: 'any', label: 'Any' }, { value: 'progressive', label: 'Progressive' }, { value: 'interlaced', label: 'Interlaced' }] },
      { key: 'fieldOrder', label: 'Field order', type: 'select', options: [{ value: 'any', label: 'Any' }, { value: 'tff', label: 'Top field first' }, { value: 'bff', label: 'Bottom field first' }] },
    ],
  },
  vfr: { group: 'format', label: 'Variable frame rate refused', source: 'probe', help: 'Phone and screen recordings often have a variable frame rate, which edits and broadcast chains do not accept.', fields: [] },
  sampleRate: { group: 'format', label: 'Audio sample rate', source: 'probe', help: 'Every audio stream.', fields: [{ key: 'allowed', label: 'Allowed', type: 'list-number', unit: 'Hz', help: 'e.g. 48000' }] },
  channels: {
    group: 'format', label: 'Channel count and layout', source: 'probe', help: 'All audio channels across streams, e.g. EBU R123: 2, 4 or 8 channels.',
    fields: [{ key: 'allowed', label: 'Allowed counts', type: 'list-number', help: 'e.g. 2, 4, 8' }, { key: 'layout', label: 'Layout', type: 'text', help: 'Optional, as FFmpeg names it: stereo, 5.1…' }],
  },
  timecode: { group: 'format', label: 'Start timecode', source: 'probe', help: 'The file carries a start timecode; with a value, it must be that one.', fields: [{ key: 'equals', label: 'Must be', type: 'text', help: 'Optional, e.g. 10:00:00:00' }] },
  duration: {
    group: 'format', label: 'Duration', source: 'probe', help: 'Within a range, or the duration a metadata field expects (a timecode or seconds), give or take the tolerance.',
    fields: [
      { key: 'minSeconds', label: 'At least', type: 'number', unit: 's', help: '0: no minimum' }, { key: 'maxSeconds', label: 'At most', type: 'number', unit: 's', help: '0: no maximum' },
      { key: 'field', label: 'Expected from field', type: 'text', help: 'A field id, e.g. f_duration' }, { key: 'toleranceSeconds', label: 'Tolerance', type: 'number', unit: 's' },
    ],
  },
  black: {
    group: 'picture', label: 'Black', source: 'decode', help: 'Black picture (blackdetect) lasting at least the minimum. Black at the head and tail is allowed up to its own allowance.',
    fields: [
      { key: 'minSeconds', label: 'Shortest', type: 'number', unit: 's' }, { key: 'pixelThreshold', label: 'Pixel threshold', type: 'number', help: '0–1 of the luma range' },
      { key: 'pictureThreshold', label: 'Share of black pixels', type: 'number', help: '0–1' },
      { key: 'headSeconds', label: 'Allowed at the head', type: 'number', unit: 's' }, { key: 'tailSeconds', label: 'Allowed at the tail', type: 'number', unit: 's' },
    ],
  },
  freeze: { group: 'picture', label: 'Freeze', source: 'decode', help: 'A still picture (freezedetect) lasting at least the minimum.', fields: [{ key: 'minSeconds', label: 'Shortest', type: 'number', unit: 's' }, { key: 'noiseDb', label: 'Noise tolerance', type: 'number', unit: 'dB' }] },
  levels: { group: 'picture', label: 'Out-of-range levels', source: 'decode', help: 'Pixels outside broadcast range (signalstats BRNG), as a share of the frame, for at least the minimum.', fields: [{ key: 'maxPercent', label: 'Most pixels out of range', type: 'number', unit: '%' }, { key: 'minSeconds', label: 'For at least', type: 'number', unit: 's' }] },
  interlace: { group: 'picture', label: 'Interlace and field order', source: 'decode', help: 'The picture as idet reads it compared with what the file declares (progressive, top or bottom field first).', fields: [] },
  letterbox: { group: 'picture', label: 'Letterbox or pillarbox', source: 'decode', help: 'Black bars on both sides of the picture (cropdetect).', fields: [{ key: 'minPixels', label: 'Bars at least', type: 'number', unit: 'px' }, { key: 'minSeconds', label: 'For at least', type: 'number', unit: 's' }] },
  loudness: {
    group: 'audio', label: 'Loudness', source: 'decode', help: 'EBU R128 integrated loudness with true peak (ebur128): the target give or take the tolerance, the highest true peak and the loudness range.',
    fields: [
      { key: 'targetLufs', label: 'Target', type: 'number', unit: 'LUFS' }, { key: 'toleranceLu', label: 'Tolerance', type: 'number', unit: 'LU' },
      { key: 'maxTruePeakDbtp', label: 'Highest true peak', type: 'number', unit: 'dBTP' }, { key: 'maxLra', label: 'Largest loudness range', type: 'number', unit: 'LU', help: '0: not checked' },
    ],
  },
  silence: {
    group: 'audio', label: 'Silence', source: 'decode', help: 'Silence (silencedetect) lasting at least the minimum, on each channel or on the mix. Silence at the head and tail is allowed up to its own allowance.',
    fields: [
      { key: 'thresholdDb', label: 'Quieter than', type: 'number', unit: 'dB' }, { key: 'minSeconds', label: 'Shortest', type: 'number', unit: 's' },
      { key: 'perChannel', label: 'Each channel', type: 'boolean' }, { key: 'headSeconds', label: 'Allowed at the head', type: 'number', unit: 's' }, { key: 'tailSeconds', label: 'Allowed at the tail', type: 'number', unit: 's' },
    ],
  },
  clipping: { group: 'audio', label: 'Clipping', source: 'decode', help: 'Samples at full scale held flat (astats, in 0.1 s windows).', fields: [{ key: 'peakDbfs', label: 'Peak at or above', type: 'number', unit: 'dBFS' }, { key: 'minSeconds', label: 'For at least', type: 'number', unit: 's' }] },
  dcOffset: { group: 'audio', label: 'DC offset', source: 'decode', help: 'A channel whose mean is off centre (astats).', fields: [{ key: 'maxPercent', label: 'Largest offset', type: 'number', unit: '%' }] },
  phase: { group: 'audio', label: 'Phase', source: 'decode', help: 'Stereo channels out of phase (aphasemeter): below the threshold for at least the minimum. −1 is opposite phase, +1 identical.', fields: [{ key: 'minPhase', label: 'Below', type: 'number' }, { key: 'minSeconds', label: 'For at least', type: 'number', unit: 's' }] },
  deadChannels: { group: 'audio', label: 'Dead or duplicated channels', source: 'decode', help: 'A channel quieter than the threshold all through (astats per channel), or a pair of channels (1–2, 3–4…) that are identical.', fields: [{ key: 'silentDb', label: 'Dead below', type: 'number', unit: 'dB RMS' }, { key: 'duplicates', label: 'Find duplicated channels', type: 'boolean' }] },
  frames: { group: 'structure', label: 'Dropped or repeated frames', source: 'packets', help: 'Gaps and duplicates in the video timestamps (an ffprobe packet scan).', fields: [{ key: 'maxGapFrames', label: 'Largest gap allowed', type: 'number', unit: 'frames' }] },
  timecodeTracks: { group: 'structure', label: 'Timecode consistency', source: 'probe', help: 'The timecode the container, the video stream and a timecode track carry agree.', fields: [] },
  avDuration: { group: 'structure', label: 'Video and audio duration', source: 'probe', help: 'The video and audio streams end together.', fields: [{ key: 'maxDifferenceSeconds', label: 'Largest difference', type: 'number', unit: 's' }] },
  subtitles: {
    group: 'subtitles', label: 'Subtitles', source: 'catalog', help: 'PRISM’s subtitle tracks: reading speed, line length, lines per cue, cue duration, the gap between cues and overlaps.',
    fields: [
      { key: 'required', label: 'A track is required', type: 'boolean' }, { key: 'maxCps', label: 'Characters per second', type: 'number' },
      { key: 'maxLineLength', label: 'Line length', type: 'number', unit: 'characters' }, { key: 'maxLines', label: 'Lines per cue', type: 'number' },
      { key: 'minDurationSeconds', label: 'Shortest cue', type: 'number', unit: 's' }, { key: 'maxDurationSeconds', label: 'Longest cue', type: 'number', unit: 's' },
      { key: 'minGapFrames', label: 'Gap between cues', type: 'number', unit: 'frames' },
    ],
  },
  metadata: { group: 'metadata', label: 'Required and needed fields', source: 'catalog', help: 'Fields the metadata model marks required or needed are filled.', fields: [{ key: 'required', label: 'Required fields', type: 'boolean' }, { key: 'needed', label: 'Needed fields', type: 'boolean' }] },
  filename: { group: 'metadata', label: 'File name', source: 'catalog', help: 'The original file name matches a pattern (a regular expression).', fields: [{ key: 'pattern', label: 'Pattern', type: 'text', help: 'e.g. ^[A-Z]{3}\\d{6}_.+\\.mxf$' }] },
};

/** Checks the media engine runs (the others are evaluated by PRISM from its catalog). */
export const QC_MEDIA_CHECKS = QC_CHECK_IDS.filter((id) => QC_CHECK_INFO[id].source !== 'catalog');

export const QcProfileInput = z.object({
  name: z.string().trim().min(1).max(120),
  description: z.string().max(1000).default(''),
  checks: QcChecksSchema,
});
export type QcProfileInput = z.infer<typeof QcProfileInput>;

export interface QcProfileDTO {
  id: string;
  name: string;
  description: string;
  checks: QcChecks;
  builtIn: boolean;
  /** A built-in profile's stable key (broadcast-hd, web, archive). */
  builtInKey: string | null;
  isDefault: boolean;
  /** Watch folders and workflows that use it. */
  usedBy: { watchFolders: number; workflows: number };
  createdAt: string;
  updatedAt: string;
}

/** Whom a QC review task goes to (1.0.0-rc.9): a role, an access group or a person. */
export const QcTaskAssigneeSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('role'), key: z.string().regex(/^[a-z0-9_]{1,40}$/) }),
  z.object({ type: z.literal('group'), id: z.string().uuid() }),
  z.object({ type: z.literal('user'), id: z.string().uuid() }),
]);
export type QcTaskAssignee = z.infer<typeof QcTaskAssigneeSchema>;

/** A review task made when QC fails: one per run, for the assignee, due after `dueHours`, and for warnings too if asked. */
export const QcReviewTaskSchema = z.object({
  assignee: QcTaskAssigneeSchema,
  dueHours: z.number().int().min(1).max(24 * 90).nullable().default(null),
  onWarnings: z.boolean().default(false),
});
export type QcReviewTask = z.infer<typeof QcReviewTaskSchema>;

/**
 * rc.8's onFailure.taskForRole (a task for each member of a role) as rc.9's review task (one task for the role); the
 * same conversion as migration 0064, so a client that still sends taskForRole keeps working.
 */
export function upgradeQcOnFailure(v: unknown): unknown {
  if (!v || typeof v !== 'object' || !('taskForRole' in v)) return v;
  const { taskForRole, ...rest } = v as { taskForRole?: unknown } & Record<string, unknown>;
  if ('task' in rest) return rest;
  return { ...rest, task: typeof taskForRole === 'string' && taskForRole ? { assignee: { type: 'role', key: taskForRole }, dueHours: null, onWarnings: false } : null };
}

/** Workspace QC settings: the default profile, QC for uploads (browser, phone and API) and what happens on failure. */
export const QcSettingsSchema = z.object({
  defaultProfileId: z.string().uuid().nullable().default(null),
  uploads: z.object({ enabled: z.boolean().default(false), profileId: z.string().uuid().nullable().default(null) }).prefault({}),
  onFailure: z.preprocess(
    (v) => upgradeQcOnFailure(v),
    z.object({ notify: z.boolean().default(true), task: QcReviewTaskSchema.nullable().default(null) }),
  ).prefault({}),
});
export type QcSettings = z.infer<typeof QcSettingsSchema>;

/** A watch folder's QC (WatchFolderSettingsSchema.qc): off unless enabled; no profile means the default profile. */
export const WatchQcSchema = z.object({
  enabled: z.boolean().default(false), profileId: z.string().uuid().optional(),
  /**
   * The review task when QC of a file from this folder fails (1.0.0-rc.9): the general setting (default), none, or
   * this folder's own task (whom, a due time, warnings).
   */
  reviewTask: z.discriminatedUnion('mode', [
    z.object({ mode: z.literal('default') }),
    z.object({ mode: z.literal('none') }),
    z.object({ mode: z.literal('assign'), task: QcReviewTaskSchema }),
  ]).optional(),
});
export type WatchQc = z.infer<typeof WatchQcSchema>;

/** The review task a failed run makes: the watch folder's own choice when it has one, else the general setting (null: none). */
export function reviewTaskFor(settings: QcSettings, watch: WatchQc['reviewTask'] | null | undefined): QcReviewTask | null {
  if (watch?.mode === 'none') return null;
  if (watch?.mode === 'assign') return watch.task;
  return settings.onFailure.task;
}

/** The review task's title: "QC review: <asset> (failed: freeze, loudness)". */
export function qcReviewTitle(title: string, status: 'failed' | 'warnings', checks: string[]): string {
  const list = [...new Set(checks)];
  const shown = list.slice(0, 4).join(', ') + (list.length > 4 ? ` and ${list.length - 4} more` : '');
  return `QC review: ${title} (${status === 'failed' ? 'failed' : 'warnings'}${shown ? `: ${shown}` : ''})`.slice(0, 200);
}

/* ---------------------------------------------------------------- built-in profiles */

const on = <T extends object>(thresholds: T = {} as T, severity?: QcSeverity) => ({ enabled: true, ...(severity ? { severity } : {}), thresholds });

/** Shipped profiles: read-only, copy one to change it. */
export const BUILTIN_QC_PROFILES: Array<{ key: string; name: string; description: string; checks: z.input<typeof QcChecksSchema> }> = [
  {
    key: 'broadcast-hd',
    name: 'Broadcast HD (EBU R128)',
    description: 'HD programme delivery: 1920×1080 at 25 or 50 fps, 48 kHz audio in 2, 4 or 8 channels (EBU R123), −23 LUFS ±1 with true peak at most −1 dBTP (EBU R128), no black, freeze, silence or clipping in the programme.',
    checks: {
      resolution: on({ allowed: ['1920x1080'] }),
      frameRate: on({ allowed: [25, 50] }),
      vfr: on(),
      sampleRate: on({ allowed: [48000] }),
      channels: on({ allowed: [2, 4, 8] }),
      timecode: on({}, 'warning'),
      black: on({ minSeconds: 1, headSeconds: 2, tailSeconds: 2 }),
      freeze: on({ minSeconds: 3 }),
      levels: on({ maxPercent: 1, minSeconds: 0.5 }, 'warning'),
      interlace: on({}, 'warning'),
      loudness: on({ targetLufs: -23, toleranceLu: 1, maxTruePeakDbtp: -1, maxLra: 20 }),
      silence: on({ thresholdDb: -60, minSeconds: 3, perChannel: true, headSeconds: 2, tailSeconds: 2 }),
      clipping: on(),
      dcOffset: on({}, 'warning'),
      phase: on({ minPhase: -0.3, minSeconds: 2 }),
      deadChannels: on(),
      frames: on(),
      timecodeTracks: on({}, 'warning'),
      avDuration: on({}, 'warning'),
      subtitles: on({}, 'warning'),
      metadata: on(),
    },
  },
  {
    key: 'web',
    name: 'Web delivery (−14 LUFS)',
    description: 'Online and social video: H.264 or HEVC in MP4 or MOV, constant frame rate, −14 LUFS ±1 with true peak at most −1 dBTP, no long black, freeze or silence.',
    checks: {
      container: on({ allowed: ['mov', 'mp4'] }),
      videoCodec: on({ allowed: ['h264', 'hevc'] }),
      vfr: on({}, 'warning'),
      sampleRate: on({ allowed: [44100, 48000] }, 'warning'),
      black: on({ minSeconds: 2, headSeconds: 1, tailSeconds: 1 }, 'warning'),
      freeze: on({ minSeconds: 4 }, 'warning'),
      loudness: on({ targetLufs: -14, toleranceLu: 1, maxTruePeakDbtp: -1 }),
      silence: on({ thresholdDb: -60, minSeconds: 4, perChannel: false, headSeconds: 1, tailSeconds: 1 }, 'warning'),
      clipping: on(),
      avDuration: on({}, 'warning'),
      subtitles: on({ maxCps: 20 }, 'warning'),
    },
  },
  {
    key: 'archive',
    name: 'Archive ingest',
    description: 'Lenient checks for incoming material: dropped frames, dead channels, clipping, long black or freeze, and required metadata. Warnings only, apart from damaged structure.',
    checks: {
      vfr: on({}, 'warning'),
      black: on({ minSeconds: 10, headSeconds: 10, tailSeconds: 10 }, 'warning'),
      freeze: on({ minSeconds: 10 }, 'warning'),
      clipping: on({}, 'warning'),
      deadChannels: on({}, 'warning'),
      frames: on({ maxGapFrames: 2 }),
      avDuration: on({ maxDifferenceSeconds: 2 }, 'warning'),
      metadata: on({ required: true, needed: false }, 'warning'),
    },
  },
];
/** The profile used when the workspace has not chosen a default. */
export const FALLBACK_QC_PROFILE_KEY = 'archive';

/* ---------------------------------------------------------------- findings and runs */

export const QcFindingSchema = z.object({
  check: z.enum(QC_CHECK_IDS as [QcCheckId, ...QcCheckId[]]),
  severity: z.enum(QC_SEVERITIES),
  /** In and out points from the first frame; null for a finding about the whole file (a format mismatch). */
  startTicks: z.number().int().nonnegative().nullable(),
  endTicks: z.number().int().nonnegative().nullable(),
  /** What was measured, e.g. {lufs: -16.1} or {channel: 2}. */
  value: z.record(z.string(), z.unknown()).nullable().default(null),
  message: z.string().min(1).max(500),
});
export type QcFinding = z.infer<typeof QcFindingSchema>;

/** What a run measured, besides its findings. */
export const QcSummarySchema = z.object({
  loudnessLufs: z.number().nullable().default(null),
  truePeakDbtp: z.number().nullable().default(null),
  loudnessRange: z.number().nullable().default(null),
  durationSeconds: z.number().nullable().default(null),
  /** Checks that ran, and those that could not (no audio for a sound check…), with why. */
  checked: z.array(z.string()).default([]),
  skipped: z.array(z.object({ check: z.string(), reason: z.string() })).default([]),
  elapsedMs: z.number().nullable().default(null),
  engine: z.string().default('prism-ffmpeg'),
  engineVersion: z.string().nullable().default(null),
  /** Findings past the limit were left out (the counts include them). */
  truncated: z.boolean().default(false),
});
export type QcSummary = z.infer<typeof QcSummarySchema>;

/** At most this many findings are recorded per run; one check at most a quarter of them. */
export const QC_MAX_FINDINGS = 500;

export const QcJobSchema = z.object({
  contractVersion: z.literal(CONTRACT_VERSION),
  correlationId: z.string(),
  qcRunId: z.string(),
  source: ObjectRefSchema,
  locations: LocationSpecsSchema,
  /**
   * The original's probe: format checks compare it with the profile, decode checks use its streams and rate. Absent
   * for the proxy or a rendition: the engine probes the file it checks.
   */
  probe: ProbeResultSchema.optional(),
  checks: QcChecksSchema,
  /** The duration a metadata field expects (duration.field), resolved by the API. */
  expectedDurationTicks: z.number().int().positive().nullable().optional(),
});
export type QcJob = z.infer<typeof QcJobSchema>;

export const QcResultSchema = z.object({
  engine: z.literal('prism-ffmpeg'),
  findings: z.array(QcFindingSchema).max(QC_MAX_FINDINGS),
  summary: QcSummarySchema,
});
export type QcResult = z.infer<typeof QcResultSchema>;

export const QC_RUN_STATUSES = ['queued', 'running', 'passed', 'warnings', 'failed', 'error'] as const;
export type QcRunStatus = (typeof QC_RUN_STATUSES)[number];
/** Statuses that mean QC was performed (a manual run is then refused on that version); `error` is a technical failure. */
export const QC_PERFORMED: readonly QcRunStatus[] = ['passed', 'warnings', 'failed'];
export const QC_TRIGGERS = ['watch', 'upload', 'workflow', 'manual'] as const;
export type QcTrigger = (typeof QC_TRIGGERS)[number];
export const QC_STATUS_LABELS: Record<QcRunStatus, string> = {
  queued: 'Queued', running: 'Running', passed: 'Passed', warnings: 'Warnings', failed: 'Failed', error: 'Error',
};

export interface QcFindingDTO extends QcFinding {
  id: string;
  /** Timecode of the in point, when the frame rate is known. */
  timecode: string | null;
  /** `byId`: who accepted it (they, or an administrator, may undo it). */
  accepted: { by: string | null; byId: string | null; at: string; comment: string } | null;
}

/** Who accepted the last open finding of a run whose findings are all accepted, and when (fixes after rc.9 testing). */
export interface QcResolution { by: string | null; at: string }

/**
 * The status a run counts as (fixes after rc.9 testing): a failed run, or one with warnings, whose every finding has
 * been accepted counts as passed. The engine's result (`status`) stays as it was, for the audit trail and for flows,
 * which branch on it when the run ends.
 */
export function qcEffectiveStatus(status: QcRunStatus, findings: number, accepted: number): QcRunStatus {
  return (status === 'failed' || status === 'warnings') && findings > 0 && accepted >= findings ? 'passed' : status;
}

/** "QC passed · findings accepted", or the status's own badge label. */
export function qcStatusLabel(status: QcRunStatus, resolved: QcResolution | null | undefined): string {
  return resolved ? 'Passed · findings accepted' : QC_STATUS_LABELS[status];
}

export interface QcRunDTO {
  id: string;
  assetId: string;
  versionId: string;
  versionNumber: number | null;
  profile: { id: string | null; name: string };
  trigger: QcTrigger;
  automationRunId: string | null;
  /** The engine's result, unchanged by accepting findings. */
  status: QcRunStatus;
  /** What the run counts as: passed when every finding of a failed or warnings run is accepted, else `status`. */
  effectiveStatus: QcRunStatus;
  /** Set when every finding was accepted: who accepted the last one, and when. */
  resolved: QcResolution | null;
  percent: number | null;
  summary: (QcSummary & { errors: number; warnings: number; accepted: number }) | null;
  error: { code: string; message: string; hint?: string } | null;
  requestedBy: string | null;
  requestedById: string | null;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  /** The run is on the asset's current version. */
  current: boolean;
  /**
   * 1.2.0: the master checked when it was not the primary (qc_runs.source {kind: 'master', representationId}); null or
   * absent: the primary (or the proxy, or a rendition).
   */
  master?: { id: string; label: string } | null;
}

/** Whether a manual "Run QC…" is possible on the current version, and why not. */
export interface QcAvailabilityDTO {
  canRun: boolean;
  reason: 'done' | 'running' | 'no_media' | 'not_media' | null;
  run: { id: string; status: QcRunStatus; effectiveStatus: QcRunStatus; resolved: QcResolution | null; finishedAt: string | null; profileId: string | null } | null;
}

/** The status a run ends with: failed with any open error, warnings with only warnings, else passed. */
export function qcOutcome(findings: Array<Pick<QcFinding, 'severity'>>): 'passed' | 'warnings' | 'failed' {
  if (findings.some((f) => f.severity === 'error')) return 'failed';
  return findings.length ? 'warnings' : 'passed';
}

/* ---------------------------------------------------------------- checks PRISM evaluates from its data */

const sec = (t: number) => t / TICKS_PER_SECOND;
const s1 = (n: number) => (Math.round(n * 100) / 100).toString();

/** Subtitle checks on PRISM's cue data (all tracks of the version). */
export function checkSubtitles(
  tracks: Array<{ label: string; cues: Cue[] }>, c: QcChecks['subtitles'], frameTicks: number, durationTicks: number | null,
): QcFinding[] {
  if (!c.enabled) return [];
  const t = c.thresholds;
  const out: QcFinding[] = [];
  const f = (start: number | null, end: number | null, message: string, value: Record<string, unknown> | null = null): QcFinding =>
    ({ check: 'subtitles', severity: c.severity, startTicks: start, endTicks: end === null || start === null ? end : Math.max(end, start + 1), value, message });
  if (!tracks.length) {
    if (t.required) out.push(f(null, null, 'No subtitle track'));
    return out;
  }
  for (const track of tracks) {
    const cues = [...track.cues].map((x) => CueSchema.parse(x)).sort((a, b) => a.startTicks - b.startTicks);
    for (let i = 0; i < cues.length; i++) {
      const cue = cues[i]!;
      const len = cue.endTicks - cue.startTicks;
      const text = visibleText(cue.text).trim();
      const at = (m: string, v: Record<string, unknown> = {}) => out.push(f(cue.startTicks, Math.max(cue.endTicks, cue.startTicks + frameTicks), `${track.label}: ${m}`, { track: track.label, cue: cue.id, ...v }));
      if (len <= 0) { at('a cue ends before it starts'); continue; }
      if (durationTicks && cue.endTicks > durationTicks + frameTicks) at('a cue runs past the end of the media');
      const next = cues[i + 1];
      if (next && next.startTicks < cue.endTicks) at('cues overlap');
      else if (next && t.minGapFrames > 0 && next.startTicks - cue.endTicks < t.minGapFrames * frameTicks && next.startTicks > cue.endTicks) {
        at(`gap to the next cue is under ${t.minGapFrames} frames`, { gapFrames: Math.round((next.startTicks - cue.endTicks) / frameTicks) });
      }
      if (t.minDurationSeconds > 0 && sec(len) < t.minDurationSeconds) at(`a cue is shorter than ${t.minDurationSeconds} s`, { seconds: Number(s1(sec(len))) });
      if (t.maxDurationSeconds > 0 && sec(len) > t.maxDurationSeconds) at(`a cue is longer than ${t.maxDurationSeconds} s`, { seconds: Number(s1(sec(len))) });
      const lines = text.split('\n');
      if (lines.length > t.maxLines) at(`more than ${t.maxLines} lines`, { lines: lines.length });
      const longest = Math.max(0, ...lines.map((l) => [...l].length));
      if (longest > t.maxLineLength) at(`a line has ${longest} characters (limit ${t.maxLineLength})`, { characters: longest });
      const cps = [...text.replace(/\n/g, ' ')].length / sec(len);
      if (cps > t.maxCps) at(`${cps.toFixed(1)} characters per second (limit ${t.maxCps})`, { cps: Number(cps.toFixed(1)) });
    }
  }
  return out;
}

/** Metadata checks: required and needed fields (given as labels of empty ones), and the file name pattern. */
export function checkMetadata(
  missing: { required: string[]; needed: string[] }, filename: string | null, c: { metadata: QcChecks['metadata']; filename: QcChecks['filename'] },
): QcFinding[] {
  const out: QcFinding[] = [];
  if (c.metadata.enabled) {
    if (c.metadata.thresholds.required && missing.required.length) {
      out.push({ check: 'metadata', severity: c.metadata.severity, startTicks: null, endTicks: null, value: { fields: missing.required }, message: `Required fields are empty: ${missing.required.join(', ')}` });
    }
    if (c.metadata.thresholds.needed && missing.needed.length) {
      out.push({ check: 'metadata', severity: c.metadata.severity, startTicks: null, endTicks: null, value: { fields: missing.needed }, message: `Needed fields are empty: ${missing.needed.join(', ')}` });
    }
  }
  if (c.filename.enabled && c.filename.thresholds.pattern) {
    let re: RegExp | null = null;
    try { re = new RegExp(c.filename.thresholds.pattern); } catch { re = null; }
    if (!re) out.push({ check: 'filename', severity: c.filename.severity, startTicks: null, endTicks: null, value: null, message: 'The file name pattern is not a valid regular expression' });
    else if (!re.test(filename ?? '')) out.push({ check: 'filename', severity: c.filename.severity, startTicks: null, endTicks: null, value: { filename }, message: `The file name “${filename ?? ''}” does not match the pattern` });
  }
  return out;
}

/** A regular expression that compiles, for the profile editor and the API. */
export function qcPatternProblem(pattern: string): string | null {
  if (!pattern) return null;
  try { new RegExp(pattern); return null; } catch (err) { return `The file name pattern is not valid: ${(err as Error).message}`; }
}
